// The coat: each cat's CoatSpec is evaluated once, in the base cat's rest pose, and baked into the
// body's UV layout. The fur shells and the body then only sample two textures, so a close-up of a
// cat with 24 fur shells costs no more for Misse's split face than for a plain coat.
//
//   colour map (sRGB): the coat colour at the roots of the hair.
//   info map (linear): r = fur length factor, g = agouti ticking strength, b = bare skin
//                      (nose leather, paw pads, inner ear), a = metres of surface per UV unit / MPU_RANGE
//                      (above 0 wherever the UV layout is covered).
import {
  type BufferGeometry, Color, DoubleSide, LinearFilter, LinearMipmapLinearFilter, Mesh, NearestFilter,
  NoColorSpace, OrthographicCamera, RepeatWrapping, Scene, ShaderMaterial, SRGBColorSpace, type Texture,
  Vector2, Vector3, Vector4, WebGLRenderTarget, type WebGLRenderer,
} from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import type { CoatPatch, CoatSpec, PatchColour } from '../../data/cats';

export const MAX_PATCHES = 16;
/** The info map stores fur length divided by this, so regions can be longer than the back. */
export const FUR_LENGTH_RANGE = 1.6;
/** The info map's alpha stores metres of surface per UV unit divided by this. */
export const MPU_RANGE = 4;

export interface CoatMaps {
  colour: Texture;
  info: Texture;
  dispose(): void;
}

/** Shared GLSL noise. */
export const NOISE_GLSL = /* glsl */ `
float cdHash3(vec3 p){ p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
float cdNoise3(vec3 x){
  vec3 i = floor(x); vec3 f = fract(x); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(cdHash3(i), cdHash3(i + vec3(1,0,0)), f.x), mix(cdHash3(i + vec3(0,1,0)), cdHash3(i + vec3(1,1,0)), f.x), f.y),
             mix(mix(cdHash3(i + vec3(0,0,1)), cdHash3(i + vec3(1,0,1)), f.x), mix(cdHash3(i + vec3(0,1,1)), cdHash3(i + vec3(1,1,1)), f.x), f.y), f.z);
}
float cdFbm(vec3 p){ return 0.5 * cdNoise3(p) + 0.28 * cdNoise3(p * 2.03 + 11.7) + 0.14 * cdNoise3(p * 4.11 + 3.1) + 0.08 * cdNoise3(p * 8.3 + 7.9); }
float cdHash2(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
`;

const BAKE_VERT = /* glsl */ `
varying vec3 vP;
varying vec3 vN;
varying vec2 vUv;
void main(){
  vP = position; vN = normal; vUv = uv;
  gl_Position = vec4(uv * 2.0 - 1.0, 0.0, 1.0);
}`;

const BAKE_FRAG = /* glsl */ `
#define MAX_PATCHES ${MAX_PATCHES}
uniform int uMode;          // 0 colour, 1 info
uniform sampler2D uDetail;  // the base model's photographic fur map, used only for fine variation
uniform float uHasDetail;
uniform vec3 uBase, uBaseLight, uDark, uWhite, uNose, uEarSkin, uPads;
uniform float uTabby, uTicking, uCountershade, uSeed, uSize;
uniform int uPatchCount;
uniform vec3 uPC[MAX_PATCHES];
uniform vec3 uPS[MAX_PATCHES];
uniform vec3 uPCol[MAX_PATCHES];
uniform vec4 uPP[MAX_PATCHES];   // shape (0 ellipsoid, 1 plane, 2 ring), edge, ragged, amount
uniform float uPM[MAX_PATCHES];  // mirrored
varying vec3 vP;
varying vec3 vN;
varying vec2 vUv;
${NOISE_GLSL}

float patchMask(int i, vec3 p, float rag){
  vec3 c = uPC[i]; vec3 s = uPS[i]; vec4 pp = uPP[i];
  float edge = max(pp.y, 1e-4);
  float r = rag * pp.z;
  if (pp.x < 0.5) {
    float d = (length((p - c) / s) - 1.0) * min(s.x, min(s.y, s.z)) + r;
    return 1.0 - smoothstep(-edge * 0.5, edge * 0.5, d);
  } else if (pp.x < 1.5) {
    float d = -dot(p - c, normalize(s)) + r;
    return 1.0 - smoothstep(-edge * 0.5, edge * 0.5, d);
  }
  float d = abs(length((p - c) / s) - 1.0) * min(s.x, min(s.y, s.z)) + abs(r) * 0.3;
  return 1.0 - smoothstep(edge * 0.5, edge, d);
}

// Mackerel tabby: rings round the torso, bars on the legs, rings on the tail, lines on the forehead.
float tabby(vec3 p){
  float w = cdFbm(p * 24.0 + uSeed) * 1.1 + cdFbm(p * 70.0) * 0.3;
  float ax = abs(p.x);
  float torsoS = sin(p.z * 118.0 + w * 5.0 + ax * 18.0 - p.y * 26.0);
  float legS = sin(p.y * 160.0 + w * 4.0 + p.z * 20.0);
  float tailS = sin(p.z * 120.0 + w * 2.5);
  float headS = sin(ax * 520.0 + w * 2.0);
  float cheekS = sin((p.y - (p.z - 0.27) * 0.6) * 520.0 + w * 2.0);
  float tailR = smoothstep(-0.25, -0.29, p.z);
  float legR = smoothstep(0.14, 0.095, p.y) * (1.0 - tailR);
  float headR = smoothstep(0.19, 0.215, p.z) * step(0.17, p.y);
  float forehead = headR * smoothstep(0.274, 0.288, p.y) * smoothstep(0.03, 0.012, ax);
  float cheek = headR * smoothstep(0.022, 0.03, ax) * smoothstep(0.278, 0.262, p.y) * smoothstep(0.24, 0.25, p.y) * smoothstep(0.29, 0.275, p.z);
  float torsoR = (1.0 - tailR) * (1.0 - legR) * (1.0 - headR) * smoothstep(0.13, 0.2, p.y);
  float s = torsoR * smoothstep(0.1, 0.75, torsoS * 0.5 + 0.5)
          + legR * smoothstep(0.35, 0.8, legS * 0.5 + 0.5) * 0.85
          + tailR * smoothstep(0.25, 0.75, tailS * 0.5 + 0.5)
          + forehead * smoothstep(0.45, 0.85, headS * 0.5 + 0.5)
          + cheek * smoothstep(0.55, 0.9, cheekS * 0.5 + 0.5) * 0.8;
  // the dorsal line is darker on most tabbies
  s = max(s, torsoR * smoothstep(0.012, 0.0, ax) * smoothstep(0.27, 0.29, p.y) * 0.8);
  return clamp(s, 0.0, 1.0);
}

void main(){
  vec3 p = vP;
  vec3 n = normalize(vN);
  float ax = abs(p.x);

  // ---- colour
  float mott = cdFbm(p * 40.0 + uSeed);
  float under = smoothstep(0.17, 0.09, p.y) * step(-0.27, p.z);
  vec3 col = mix(uBase, uBaseLight, clamp(mott * 0.45 + uCountershade * under, 0.0, 1.0));
  col *= 1.0 - 0.12 * smoothstep(0.24, 0.29, p.y) * (1.0 - under); // darker along the back
  col = mix(col, uDark, uTabby * tabby(p) * 0.85);

  float rag = (cdFbm(p * 150.0 + 3.0) - 0.5) * 2.0 + (cdFbm(p * 45.0 + 8.0) - 0.5) * 1.5;
  float whiteness = 0.0;
  for (int i = 0; i < MAX_PATCHES; i++) {
    if (i >= uPatchCount) break;
    float m = patchMask(i, p, rag);
    if (uPM[i] > 0.5) m = max(m, patchMask(i, p * vec3(-1.0, 1.0, 1.0), rag));
    m *= uPP[i].w;
    col = mix(col, uPCol[i], m);
    float isWhite = 1.0 - step(0.01, dot(abs(uPCol[i] - uWhite), vec3(1.0)));
    whiteness = mix(whiteness, isWhite, m);
  }

  // fine photographic fur variation, high-passed so it adds texture but not the source cat's colours
  if (uHasDetail > 0.5) {
    float l0 = dot(texture2D(uDetail, vUv).rgb, vec3(0.3, 0.59, 0.11));
    float l1 = dot(textureLod(uDetail, vUv, 5.0).rgb, vec3(0.3, 0.59, 0.11));
    col *= clamp(mix(1.0, l0 / max(l1, 0.02), 0.12), 0.94, 1.06);
  }

  // ---- bare skin: nose leather, inner ears, paw pads
  // nose leather: a small rounded triangle, wider at the top
  vec3 nq = p - vec3(0.0, 0.2478, 0.3172);
  float noseW = 0.0058 * (1.0 + clamp(nq.y / 0.008, -0.6, 0.4));
  float nose = 1.0 - smoothstep(0.85, 1.15, length(nq / vec3(noseW, 0.0042, 0.0048)));
  nose = max(nose, (1.0 - smoothstep(0.8, 1.1, length((p - vec3(0.0, 0.2415, 0.3195)) / vec3(0.0011, 0.0035, 0.004)))) * 0.35); // philtrum
  float earR = smoothstep(0.287, 0.297, p.y) * smoothstep(0.018, 0.026, ax) * smoothstep(0.236, 0.246, p.z) * smoothstep(0.282, 0.272, p.z);
  float innerEar = earR * smoothstep(0.05, 0.45, n.z) * smoothstep(0.322, 0.305, p.y);
  float pad = smoothstep(0.012, 0.004, p.y) * smoothstep(-0.15, -0.6, n.y);
  if (uHasDetail > 0.5) {
    vec3 t = texture2D(uDetail, vUv).rgb;
    float pink = smoothstep(0.05, 0.16, t.r - t.g) * smoothstep(0.25, 0.45, dot(t, vec3(0.33)));
    pad *= mix(0.35, 1.0, pink);
  }
  float lip = smoothstep(0.0012, 0.0, abs(p.y - 0.2325 + (0.315 - p.z) * 0.25)) * smoothstep(0.3, 0.31, p.z) * smoothstep(0.012, 0.004, ax);
  col = mix(col, mix(uDark, vec3(0.06, 0.04, 0.04), 0.5), lip * 0.5);
  // dark eyelid rims and sockets, so the eye opening reads cleanly
  vec3 q = vec3(ax, p.y, p.z);
  float eyeD = distance(q, vec3(0.0184, 0.265, 0.2885));
  float socket = 1.0 - smoothstep(0.0128, 0.0146, eyeD);
  col = mix(col, mix(uDark, vec3(0.025, 0.018, 0.015), 0.7), socket);
  // inner ear: muted pink skin, paler with furnishings towards the rim
  float earEdge = smoothstep(0.31, 0.32, p.y) + smoothstep(0.03, 0.045, ax) * 0.5;
  vec3 earCol = mix(uEarSkin, col, clamp(earEdge, 0.0, 1.0) * 0.5);
  col = mix(col, earCol, innerEar * 0.75);
  col = mix(col, uPads, pad);
  col = mix(col, uNose, nose);
  float skin = max(nose, max(pad, innerEar * 0.7));

  // ---- fur length: none over the eyes and nose, short on the face, ears and legs
  float eye = smoothstep(0.0132, 0.0165, eyeD);
  float face = smoothstep(0.18, 0.27, p.z) * smoothstep(0.13, 0.21, p.y);
  float legs = smoothstep(0.11, 0.04, p.y);
  float paws = smoothstep(0.03, 0.01, p.y);
  float tail = smoothstep(-0.27, -0.32, p.z);
  float ruff = smoothstep(0.24, 0.2, p.z) * smoothstep(0.12, 0.18, p.z) * smoothstep(0.24, 0.16, p.y);
  // A short coat throughout: the face still has shorter hair, with a gradual neck blend.
  float len = 0.55;
  len *= mix(1.0, 0.45, face) * mix(1.0, 0.65, legs) * mix(1.0, 0.65, paws);
  len *= 1.0 + 0.12 * tail + 0.15 * ruff + 0.08 * under;
  len *= mix(1.0, 0.35, earR);
  len *= eye * (1.0 - skin);

  if (uMode == 0) gl_FragColor = vec4(max(col, vec3(0.0)), 1.0);
  else {
    // metres of surface per unit of UV here, so the fur can keep its hairs the same size everywhere
    float mpu = 0.5 * (length(dFdx(vP)) + length(dFdy(vP))) * uSize;
    gl_FragColor = vec4(clamp(len / ${FUR_LENGTH_RANGE.toFixed(2)}, 0.0, 1.0), uTicking * mix(1.0, 0.35, face) * (1.0 - whiteness) * (1.0 - skin), skin, clamp(mpu / ${MPU_RANGE.toFixed(1)}, 0.02, 1.0));
  }
}`;

const DILATE_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform vec2 uTexel;
varying vec2 vUv;
void main(){
  vec4 c = texture2D(tSrc, vUv);
  if (c.a > 0.0) { gl_FragColor = c; return; }
  vec4 s = vec4(0.0); float n = 0.0;
  for (int i = -1; i <= 1; i++) for (int j = -1; j <= 1; j++) {
    vec4 t = texture2D(tSrc, vUv + vec2(float(i), float(j)) * uTexel);
    if (t.a > 0.0) { s += t; n += 1.0; }
  }
  gl_FragColor = n > 0.0 ? s / n : vec4(0.0);
}`;

const QUAD_VERT = /* glsl */ `varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

function linear(hex: string): Vector3 {
  const c = new Color(hex); // three converts sRGB hex to the linear working space
  return new Vector3(c.r, c.g, c.b);
}

function patchColour(coat: CoatSpec, c: PatchColour): string {
  if (c === 'white') return coat.white;
  if (c === 'dark') return coat.dark;
  if (c === 'base') return coat.base;
  return c;
}

const SHAPES: Record<CoatPatch['shape'], number> = { ellipsoid: 0, plane: 1, ring: 2 };

/** Uniform values for a coat, shared by the bake. Exported for tests and tooling. */
export function coatUniforms(coat: CoatSpec, seed: number) {
  if (coat.patches.length > MAX_PATCHES) throw new Error(`coat: at most ${MAX_PATCHES} markings`);
  const pad = <T>(arr: T[], fill: () => T) => { while (arr.length < MAX_PATCHES) arr.push(fill()); return arr; };
  return {
    uBase: { value: linear(coat.base) },
    uBaseLight: { value: linear(coat.baseLight) },
    uDark: { value: linear(coat.dark) },
    uWhite: { value: linear(coat.white) },
    uNose: { value: linear(coat.nose) },
    uEarSkin: { value: linear(coat.earSkin) },
    uPads: { value: linear(coat.pads) },
    uTabby: { value: coat.tabby },
    uTicking: { value: coat.ticking },
    uCountershade: { value: coat.countershade },
    uSeed: { value: seed },
    uPatchCount: { value: coat.patches.length },
    uPC: { value: pad(coat.patches.map((p) => new Vector3(...p.centre)), () => new Vector3()) },
    uPS: { value: pad(coat.patches.map((p) => new Vector3(...p.size)), () => new Vector3(1, 1, 1)) },
    uPCol: { value: pad(coat.patches.map((p) => linear(patchColour(coat, p.colour))), () => new Vector3()) },
    uPP: { value: pad(coat.patches.map((p) => new Vector4(SHAPES[p.shape], p.edge, p.ragged ?? 0, p.amount ?? 1)), () => new Vector4()) },
    uPM: { value: pad(coat.patches.map((p) => (p.mirror ? 1 : 0)), () => 0) },
  };
}

function makeTarget(size: number, srgb: boolean, mips: boolean): WebGLRenderTarget {
  const rt = new WebGLRenderTarget(size, size, {
    depthBuffer: false,
    generateMipmaps: mips,
    minFilter: mips ? LinearMipmapLinearFilter : NearestFilter,
    magFilter: mips ? LinearFilter : NearestFilter,
    wrapS: RepeatWrapping, wrapT: RepeatWrapping,
  });
  rt.texture.colorSpace = srgb ? SRGBColorSpace : NoColorSpace;
  return rt;
}

/**
 * Bakes a coat into the body's UV layout.
 * `geometry` is the base cat's body geometry (rest-pose positions, normals and uv).
 * `detail` is the base model's own fur map; only its fine variation is used.
 */
export function bakeCoat(renderer: WebGLRenderer, geometry: BufferGeometry, coat: CoatSpec, detail: Texture | null, seed = 0, size = 2048): CoatMaps {
  const mat = new ShaderMaterial({
    vertexShader: BAKE_VERT,
    fragmentShader: BAKE_FRAG,
    uniforms: { ...coatUniforms(coat, seed), uMode: { value: 0 }, uSize: { value: size }, uDetail: { value: detail }, uHasDetail: { value: detail ? 1 : 0 } },
    side: DoubleSide, depthTest: false, depthWrite: false,
  });
  const scene = new Scene();
  const mesh = new Mesh(geometry, mat);
  mesh.frustumCulled = false;
  scene.add(mesh);
  const cam = new OrthographicCamera();
  const dilate = new ShaderMaterial({
    vertexShader: QUAD_VERT, fragmentShader: DILATE_FRAG,
    uniforms: { tSrc: { value: null }, uTexel: { value: new Vector2(1 / size, 1 / size) } },
    depthTest: false, depthWrite: false,
  });
  const quad = new FullScreenQuad(dilate);

  const prevTarget = renderer.getRenderTarget();
  const prevClear = renderer.getClearColor(new Color());
  const prevAlpha = renderer.getClearAlpha();
  const prevAuto = renderer.autoClear;
  renderer.autoClear = false;
  renderer.setClearColor(0x000000, 0);

  const bakeOne = (mode: number, srgb: boolean): WebGLRenderTarget => {
    const a = makeTarget(size, srgb, false);
    const b = makeTarget(size, srgb, false);
    const out = makeTarget(size, srgb, true);
    mat.uniforms.uMode!.value = mode;
    renderer.setRenderTarget(a); renderer.clear(); renderer.render(scene, cam);
    let src = a, dst = b;
    const passes = 16; // grows the islands by 16 texels so mipmaps and filtering never see the gaps
    for (let i = 0; i < passes; i++) {
      dilate.uniforms.tSrc!.value = src.texture;
      const target = i === passes - 1 ? out : dst;
      renderer.setRenderTarget(target); renderer.clear(); quad.render(renderer);
      [src, dst] = [dst, src];
    }
    a.dispose(); b.dispose();
    return out;
  };

  const colour = bakeOne(0, true);
  const info = bakeOne(1, false);

  renderer.setRenderTarget(prevTarget);
  renderer.setClearColor(prevClear, prevAlpha);
  renderer.autoClear = prevAuto;
  mat.dispose(); dilate.dispose(); quad.dispose();

  colour.texture.anisotropy = 8;
  return {
    colour: colour.texture,
    info: info.texture,
    dispose() { colour.dispose(); info.dispose(); },
  };
}
